if (!customElements.get('predictive-search')) {
  const oleaPredictiveCache = new Map();
  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('[data-input]');
      this.panelInput = this.querySelector('[data-panel-input]');
      this.results = this.querySelector('[data-results]');
      this.body = this.querySelector('[data-search-body]');
      this.status = this.querySelector('[data-status]');
      this.clearButton = this.querySelector('[data-clear]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      if (!this.input || !this.results || !this.body) return;
      this.historyKey = `olea-search-history:${document.documentElement.lang || 'fr'}`;
      this.events = new AbortController();
      const signal = this.events.signal;
      document.addEventListener('olea:header-panel-open', event => { if (event.detail.source !== this && event.detail.source?.search !== this.closest('dialog')) this.close(); }, { signal });
      document.addEventListener('olea:header-panel-close', event => {
        const wasOpen = !this.results.hidden;
        this.close({ restoreFocus: wasOpen && event.detail.restoreFocus });
      }, { signal });
      window.Olea.watchMedia(this.mobile, () => this.close({ immediate: true }), { signal });
      this.addEventListener('input', event => {
        if (event.target !== this.input && event.target !== this.panelInput) return;
        this.syncQuery(event.target.value);
        if (!event.isComposing) this.queueSearch();
      }, { signal });
      const revealExisting = () => {
        if (this.restoringFocus) return;
        const term = this.input.value.trim();
        if (!term) this.showDefault();
        this.open(true);
        if (term && this.lastTerm !== term) this.queueSearch();
      };
      this.input.addEventListener('focus', revealExisting, { signal });
      this.input.addEventListener('pointerdown', revealExisting, { signal });
      this.addEventListener('submit', () => this.rememberQuery(), { signal });
      this.addEventListener('click', event => {
        if (event.target.closest('[data-predictive-close]')) {
          this.dismiss();
        }
        if (event.target.closest('[data-panel-clear]')) {
          this.syncQuery('');
          this.queueSearch();
          this.panelInput?.focus({ preventScroll: true });
        }
        const recent = event.target.closest('[data-search-recent]');
        if (recent) {
          this.syncQuery(recent.dataset.searchRecent);
          this.input.focus({ preventScroll: true });
          this.queueSearch();
        }
        if (event.target.closest('[data-predictive-link], .product-card a[href]')) this.rememberQuery();
        const assistant = event.target.closest('[data-assistant-open]');
        if (assistant) assistant.dataset.assistantQuery = this.input.value.trim();
      }, { signal });
      this.clearButton?.addEventListener('click', () => {
        this.syncQuery('');
        this.queueSearch();
        this.open(true);
      }, { signal });
      this.addEventListener('focusin', event => {
        const rail = event.target.closest('.search-panel__products');
        const card = event.target.closest('.product-card');
        if (!this.mobile.matches || !rail || !card || rail.scrollWidth <= rail.clientWidth + 1) return;
        const bounds = rail.getBoundingClientRect(), rect = card.getBoundingClientRect();
        if (rect.left < bounds.left || rect.right > bounds.right) card.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: document.body.classList.contains('animations-enabled') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
      }, { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.addEventListener('focusout', () => setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0), { signal });
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, { signal });
      this.syncQuery(this.input.value);
      this.showDefault();
    }
    disconnectedCallback() {
      this.close({ immediate: true });
      window.OleaHeaderPanels?.release(this);
      clearTimeout(this.debounce);
      this.controller?.abort();
      this.events?.abort();
      this.initialized = false;
    }
    syncQuery(value) {
      this.input.value = value;
      if (this.panelInput) this.panelInput.value = value;
      this.clearButton.hidden = !value;
      const panelClear = this.querySelector('[data-panel-clear]');
      if (panelClear) panelClear.hidden = !value;
      this.updatePanelIdentity();
    }
    updatePanelIdentity() {
      const title = this.querySelector('[data-search-panel-title]');
      const fragment = this.body?.querySelector('[data-predictive-fragment]');
      const query = this.input.value.trim();
      if (title) title.textContent = query ? title.dataset.resultTitle.replace('__query__', query) : title.dataset.emptyTitle;
      const count = this.querySelector('[data-search-panel-count]');
      if (count) count.textContent = query && fragment?.dataset.query === query ? fragment.dataset.countLabel : '';
      const view = this.querySelector('[data-search-view-all]');
      if (view) {
        const target = new URL(this.querySelector('.predictive-search__form').action, location.origin);
        target.searchParams.set('q', query);
        target.searchParams.set('type', 'product');
        target.searchParams.set('options[prefix]', 'last');
        view.href = target.pathname + target.search;
        const label = view.querySelector('span');
        const resultCount = Number(fragment?.dataset.productsCount || 0);
        const currentResults = query && fragment?.dataset.query === query && Number.isSafeInteger(resultCount) && resultCount >= 0;
        const resultLabel = String(resultCount) + (resultCount >= 10 ? '+' : '');
        if (label) label.textContent = currentResults ? view.dataset.countLabel.replace('__count__', resultLabel) : view.dataset.idleLabel;
      }
    }
    completeNativeResults(fragment) {
      const category = fragment.querySelector('.search-panel__category');
      if (category) {
        const path = new URL(category.getAttribute('href'), location.origin).pathname.replace(/\/$/, '');
        const levels = document.querySelectorAll('smart-header [data-mobile-level][data-title]');
        for (const level of levels) {
          if (level.dataset.mobileLevel === 'root') continue;
          const matches = [...level.querySelectorAll('a[href]')].some(link => new URL(link.getAttribute('href'), location.origin).pathname.replace(/\/$/, '') === path);
          if (matches && level.dataset.title) { category.querySelector('strong').textContent = level.dataset.title; break; }
        }
      }
      const section = fragment.querySelector('[data-search-suggestions]');
      const list = section?.querySelector('[data-search-suggestion-list]');
      if (!list || list.childElementCount) return;
      const term = this.input.value.trim();
      if (!term) return;
      const fold = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
      const candidates = [], seen = new Set([fold(term)]);
      const add = value => { const key = fold(value); if (!seen.has(key) && value.length <= 150) { seen.add(key); candidates.push(value); } };
      for (const card of fragment.querySelectorAll('.approved-panel-product')) {
        const title = card.querySelector('.product-card__title')?.textContent.trim() || '';
        const shade = card.querySelector('.approved-panel-product__color')?.textContent.trim() || '';
        if (!fold(title).startsWith(fold(term))) continue;
        const completion = title.slice(term.length).match(/^\S+/u)?.[0] || '';
        const stem = term + completion;
        const rest = title.slice(stem.length).trim();
        const size = rest.match(/\b\d+(?:[.,]\d+)?\s+[^\s]+/u)?.[0];
        if (size) add(`${stem} ${size}`);
        const family = (size ? rest.replace(size, '') : rest).split(/\s+/u).find(word => word && !/^\d|^(?:de|du|des|le|la|les|the|of|with|en)$/i.test(word));
        if (family) add(`${stem} ${family}`);
        if (shade) add(`${stem} ${shade.split(/\s+/u)[0].toLocaleLowerCase()}`);
        if (!candidates.length && rest) add(`${stem} ${rest}`);
        if (candidates.length >= 3) break;
      }
      for (const suggestion of candidates.slice(0, 3)) {
        const item = document.createElement('li'), link = document.createElement('a');
        const url = new URL(this.querySelector('.predictive-search__form').action, location.origin);
        url.searchParams.set('q', suggestion); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last');
        link.href = url.pathname + url.search; link.dataset.predictiveLink = '';
        const icon = window.Olea.createIcon('search'); if (icon) link.append(icon);
        const label = document.createElement('span'), completion = document.createElement('strong');
        label.append(document.createTextNode(suggestion.slice(0, term.length)));
        completion.textContent = suggestion.slice(term.length); label.append(completion);
        const arrow = document.createElement('span'); arrow.className = 'search-panel__suggestion-arrow'; arrow.setAttribute('aria-hidden', 'true'); arrow.textContent = '↖';
        link.append(label, arrow); item.append(link); list.append(item);
      }
      section.hidden = !list.childElementCount;
    }
    queueSearch() {
      clearTimeout(this.debounce);
      this.controller?.abort();
      const term = this.input.value.trim();
      if (!term) {
        this.lastTerm = '';
        this.removeAttribute('aria-busy');
        this.status.textContent = '';
        this.showDefault();
        return;
      }
      this.debounce = setTimeout(() => this.search(term), 160);
    }
    showDefault() {
      const template = this.querySelector('[data-predictive-default]');
      if (template) this.body.replaceChildren(template.content.cloneNode(true));
      this.renderRecents();
      this.updatePanelIdentity();
    }
    readRecents() {
      try {
        const items = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
        return Array.isArray(items) ? items.filter(item => typeof item === 'string' && item.length >= 2 && item.length <= 100).slice(0, 6) : [];
      } catch { return []; }
    }
    rememberQuery() {
      const term = this.input.value.trim().slice(0, 100);
      if (term.length < 2) return;
      const items = this.readRecents().filter(item => item.toLocaleLowerCase() !== term.toLocaleLowerCase());
      try { localStorage.setItem(this.historyKey, JSON.stringify([term, ...items].slice(0, 6))); } catch { /* Search works when browser storage is unavailable. */ }
    }
    renderRecents() {
      const container = this.body.querySelector('[data-search-recents]');
      const empty = this.body.querySelector('[data-search-recents-empty]');
      if (!container) return;
      const items = this.readRecents();
      container.replaceChildren(...items.map(term => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.searchRecent = term;
        const clock = document.createElement('span');
        clock.className = 'search-panel__clock';
        clock.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.textContent = term;
        button.append(clock, label);
        return button;
      }));
      if (empty) empty.hidden = items.length > 0;
    }
    open(focusInput = false) {
      const reveal = () => {
        if (!this.isConnected) return;
        clearTimeout(this.closeTimer);
        this.closeRestoreFocus = false;
        this.results.classList.remove('is-closing');
        this.results.inert = false;
        document.querySelectorAll('predictive-search').forEach(instance => { if (instance !== this && instance.close) instance.close(); });
        window.OleaOverlayMotion.open(this.results, () => { this.results.hidden = false; });
        this.input.setAttribute('aria-expanded', 'true');
        this.renderRecents();
        if (focusInput) (this.mobile.matches && this.panelInput ? this.panelInput : this.input).focus({ preventScroll: true });
      };
      if (this.closest('smart-header') && window.OleaHeaderPanels) return window.OleaHeaderPanels.open('search', this, reveal);
      reveal();
    }
    restoreInputFocus() {
      this.restoringFocus = true;
      this.input.focus({ preventScroll: true });
      this.restoringFocus = false;
    }
    dismiss() {
      const dialog = this.closest('.header-search-dialog');
      const header = this.closest('smart-header');
      if (dialog?.open && header?.closeDialog) header.closeDialog(dialog);
      else this.close({ restoreFocus: true });
    }
    closeForHeader() { return this.close({ restoreFocus: false }); }
    close({ immediate = false, restoreFocus = false } = {}) {
      window.OleaHeaderPanels?.cancel(this);
      if (!this.results || !this.input) return Promise.resolve(true);
      if (!this.results.hidden && this.lastTerm === this.input.value.trim()) this.rememberQuery();
      clearTimeout(this.debounce);
      this.controller?.abort();
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('aria-busy');
      this.closeRestoreFocus ||= restoreFocus;
      this.results.inert = true;
      return window.OleaOverlayMotion.close(this.results, () => this.finishClose(), { immediate });
    }
    finishClose() {
      clearTimeout(this.closeTimer);
      this.results.hidden = true;
      this.results.classList.remove('is-closing');
      this.results.removeAttribute('aria-modal');
      this.results.inert = false;
      window.OleaHeaderPanels?.release(this);
      if (this.closeRestoreFocus && this.input.isConnected) this.restoreInputFocus();
      this.closeRestoreFocus = false;
    }
    onKeydown(event) {
      if (event.key === 'Escape' && !this.results.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.dismiss();
        return;
      }
      if (event.key === 'Tab' && this.mobile.matches && !this.results.hidden && !this.results.inert) {
        const focusable = [...this.querySelectorAll('a[href],button,input,[tabindex="0"]')].filter(node => !node.disabled && node.type !== 'hidden' && !node.closest('[hidden]') && node.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (first && (event.shiftKey && document.activeElement === first || !event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.results.hidden) return;
      const links = [...this.body.querySelectorAll('a[href],button:not(:disabled)')].filter(node => !node.closest('[hidden]'));
      if (!links.length) return;
      event.preventDefault();
      const current = links.indexOf(document.activeElement);
      if (event.key === 'ArrowUp' && current <= 0) return this.input.focus({ preventScroll: true });
      const next = event.key === 'ArrowDown' ? Math.min(current + 1, links.length - 1) : current - 1;
      links[next]?.focus();
    }
    async search(term) {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      const url = new URL(this.dataset.url, window.location.origin);
      const current = new URL(window.location.href);
      ['preview_theme_id', 'pb', 'currency'].forEach(name => { if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name)); });
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,collection,article,page,query');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'predictive-search');
      const cacheKey = url.href;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      try {
        let html = oleaPredictiveCache.get(cacheKey);
        if (!html) {
          const response = await fetch(url, { signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error(String(response.status));
          html = await response.text();
          oleaPredictiveCache.set(cacheKey, html);
          if (oleaPredictiveCache.size > 16) oleaPredictiveCache.delete(oleaPredictiveCache.keys().next().value);
        }
        if (controller.signal.aborted || this.input.value.trim() !== term) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('[data-predictive-fragment]');
        if (!fragment) throw new Error('Missing predictive section');
        this.body.replaceChildren(fragment);
        this.completeNativeResults(fragment);
        this.updatePanelIdentity();
        this.renderRecents();
        this.lastTerm = term;
        this.status.textContent = fragment.dataset.countLabel;
        if (this.contains(document.activeElement)) this.open();
      } catch (reason) {
        if (reason.name === 'AbortError' || controller.signal.aborted) return;
        this.showDefault();
        const message = document.createElement('p');
        message.className = 'search-panel__empty';
        message.textContent = this.dataset.error;
        this.body.querySelector('.search-panel__empty')?.replaceWith(message);
        this.status.textContent = this.dataset.error;
        if (this.contains(document.activeElement)) this.open();
      } finally {
        if (!controller.signal.aborted) this.removeAttribute('aria-busy');
      }
    }
  });
}
